import { useMemo } from 'react';
import { Flex, GoodsCard, useScreenSize } from '@banhala/ui-kit';
import { PrimaryCard } from '../TwoColCardList/PrimaryCard';
import type { RowItem } from '../../types';

const COLUMN_GAP = 2;
const COLUMN_COUNT = 2;

/**
 * 2개 상품을 한 행으로 렌더링 (List 중첩 없이 단일 FlatList에서 가상화)
 *
 * 기존 TwoColGoodsList/TwoColCardList는 내부에 List(numColumns=2)를 사용하여
 * 수직×수직 List 중첩이 발생 → 가상화 무력화.
 * 이 컴포넌트는 2개 아이템을 Flex.Row + gap으로 렌더링하여 중첩을 제거한다.
 */
export const TwoColGoodsRow = ({
  items,
  onPressItem,
  onPressLike,
  likeMap,
}: {
  items: RowItem[];
  onPressItem: (item: RowItem) => void;
  onPressLike: (item: RowItem) => Promise<void>;
  likeMap: Record<string, boolean>;
}) => {
  const { width: screenWidth } = useScreenSize();
  // PrimaryCard가 width를 필수로 요구하므로 명시적 계산 유지
  const itemWidth = useMemo(
    () => (screenWidth - COLUMN_GAP * (COLUMN_COUNT - 1)) / COLUMN_COUNT,
    [screenWidth],
  );

  return (
    <Flex.Row gap={2}>
      {items.map((item, i) => {
        if (item.type === 'placeholder') {
          return <Flex key={`ph-${i}`} flex={1} />;
        }
        if (item.type === 'primaryCard') {
          return (
            <PrimaryCard
              key={item.key}
              item={item.item}
              logging={item.logging}
              width={itemWidth}
            />
          );
        }
        return (
          <GoodsCard
            key={`${item.componentIndex}-${item.entityIndex}`}
            data={item.data}
            size="medium"
            testID={`goods/${item.sno}`}
            defaultWidth={itemWidth}
            onPress={() => onPressItem(item)}
            onPressLike={async () => {
              await onPressLike(item);
            }}
            isLike={likeMap[item.sno]}
          />
        );
      })}
    </Flex.Row>
  );
};
